import {
  Badge,
  Button, Col, Dropdown, Menu, Modal, Popover, Row, Tag, Tooltip, Typography,
} from '@revfluence/fresh';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { InfoCircleFilled, SettingOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import { useHistory, useRouteMatch } from 'react-router-dom';
import LinkSimpleIcon from '@revfluence/fresh-icons/regular/esm/LinkSimpleIcon';
import { TvIcon } from '@revfluence/fresh-icons/regular';
import { CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import { ShieldCheckIcon } from '@revfluence/fresh-icons/solid/esm';
import {
  first, isEmpty, isNull, capitalize,
  find,
  chain,
} from 'lodash';
import { format } from 'date-fns';
import { STAPaymentApp } from '@frontend/applications/AffiliatesApp/containers';
import { DateFilter, IDateRangeSettings } from '@frontend/app/components';
import { CLIENT_CONNECTION_STATUS, OFFER_PRICE_RULE_TYPE, OFFER_PROMO_USAGE_LIMIT_RULE, OFFER_SOURCE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import moment from 'moment';
import { ShopifyIcon } from '@revfluence/fresh-icons/brands/esm';
import { GetOfferById_offer, GetOfferById_offer_promos } from '@frontend/applications/AffiliatesApp/queries/types/GetOfferById';
import {
  BoxArchiveIcon, ChartBarIcon, ClockRotateLeftIcon,
  SlidersIcon,
} from '@revfluence/fresh-icons/regular/esm';
import { useApolloClientForAspirex } from '@frontend/applications/AffiliatesApp/hooks/useApolloClientForAspirex';
import { GET_ALL_PROGRAMS } from '@frontend/app/queries';
import { GetAllPrograms_programs } from '@frontend/app/queries/types/GetAllPrograms';
import { CalendarIcon, EllipsisIcon } from '@revfluence/fresh-icons/solid';
import { useArchiveOfferMutation } from '@frontend/applications/AffiliatesApp/hooks/useArchiveOfferMutation';
import { message } from 'antd';
import HeaderButton from '@frontend/app/refresh-components/HeaderButton';
import { ArrowLeft } from 'lucide-react';
import { Button as ShadCnBtn } from '@frontend/shadcn/components/ui/button';
import { Small } from '@frontend/shadcn/components/typography/small';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import ShadCnTag from '@frontend/app/refresh-components/Tag';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@frontend/shadcn/components/ui/accordion';
import {
  Tooltip as ShadCnTooltip, TooltipTrigger, TooltipContent,
  TooltipProvider,
} from '@frontend/shadcn/components/ui/tooltip';
import styles from './OfferDetailCard.scss';
import { ConversionTrackingModal } from '../../ConversionTrackingModal';
import { OfferImage } from '../../OfferImage';
import { OfferLogDrawer } from '../../OfferLogDrawer';
import { logger } from '../../../../../../../common/Logger';
import { OfferConversionHistoryDrawer } from '../../OfferConversionHistoryDrawer';
import { OFFER_STATUS } from '../../OfferTable';
import DeletedMembersDrawer from '../../DeletedMemberDrawer/DeletedMemberDrawer';
import { TOfferMember } from '../../MemberTable/types';
import { RefreshOfferDefaultImage } from '../../OfferImage/RefreshOfferDefaultImage';
import { PROMO_OFFER_TYPES, PurchaseType, RecurringCycleLimit } from '../../OfferForm';
import { getDiscountCombinationMessage, getPayoutLabel, getUniqueRefreshTagColor, getUsageLimitMessage } from '../../OfferForm/utils/OfferFormUtils';

interface IProps {
  clientId?: string;
  clientName?: string;
  dateRangeSettings?: IDateRangeSettings;
  showCta?: boolean;
  isWorkFlow?: boolean;
  infoButtonClassName?: boolean; // Define the class name for the 'info' button and specify its position relative to the 'offer detail.
  offerData?: GetOfferById_offer;
  archiveOffer?: boolean;
  refreshUi?: boolean;
  goBack?: () => void;
  hideOfferName?: boolean
  deletedMembers?: readonly TOfferMember[];
  setIsMemberTableFilterOpen?: (open: boolean) => void;
}

const { Title, Text } = Typography;

const getOfferStatusColor = (status): string => {
  switch (status) {
    case OFFER_STATUS.ACTIVE:
      return '#4eb468';
    case OFFER_STATUS.PAUSED:
      return '#eacd60';
    case OFFER_STATUS.EXPIRED:
      return '#f1515f';
    default:
      return '#4eb468';
  }
};

const getDiscountText = (
  promoOffer: GetOfferById_offer_promos,
  isReadOnly: boolean,
  status?: OFFER_STATUS
) => {
  if (promoOffer) {
    if (isReadOnly) {
      return status ? `${status} - Buy X, Get Y` : 'Buy X, Get Y';
    }

    if (promoOffer.priceRuleType === OFFER_PRICE_RULE_TYPE.AMOUNT) {
      return status ? `${status} - $${promoOffer.priceRuleAmount} Discount` : `$${promoOffer.priceRuleAmount} Discount`;
    }

    if (promoOffer.priceRuleType === OFFER_PRICE_RULE_TYPE.PERCENTAGE) {
      return status ? `${status} - ${promoOffer.priceRuleAmount}% Discount` : `${promoOffer.priceRuleAmount}% Discount`;
    }
  } else {
    return capitalize(status);
  }
};

// Helper to truncate string to n chars with ...
function truncateText(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

// Custom hook to get window width
function useWindowWidth(): number {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
}

// Offer details nav bar UI with offer details info button and show button(payment overview and Date filter)
export const OfferDetailCard: React.FC<Readonly<IProps>> = (props) => {
  const windowWidth = useWindowWidth();
  const {
    clientId, clientName, dateRangeSettings, showCta, infoButtonClassName, isWorkFlow, offerData, refreshUi, goBack,
    hideOfferName, deletedMembers, setIsMemberTableFilterOpen,
  } = props;
  const [visible, setVisible] = useState(false);
  const [isOfferLogDrawerVisible, setIsOfferLogDrawerVisible] = useState(false);
  const [instructionsVisible, updateInstructionsVisibility] = useState(false);
  const [archiveOfferModal, setArchiveOfferModal] = React.useState(false);
  const [archiveOfferLoading, setArchiveOfferLoading] = React.useState(false);
  const [isOfferConversionHistoryDrawerVisible, setIsOfferConversionHistoryDrawerVisible] = useState(false);
  const [programs, setPrograms] = useState<GetAllPrograms_programs[]>();
  const [isDeletedMembersDrawerVisible, setIsDeletedMembersDrawerVisible] = useState(false);
  const history = useHistory();
  const match = useRouteMatch();
  const baseUri = match.url;
  const aspirexApolloClient = useApolloClientForAspirex();
  const [archiveOfferMutation] = useArchiveOfferMutation();
  const { earliestDate } = dateRangeSettings || {};
  const { migrateToGraphQL, archiveOffer, deleteCreatorFromOffer, refreshSTADashboard } = useClientFeatures();
  const {
    offer: contextOffer, setShowMigrationModal, disableOfferEdit, profile,
  } = useOfferDetailsContext() || {}; // if calling from Affiliates app then fetch the offer from context otherwise pass the offer detail as prop
  let offer = contextOffer;
  if (isWorkFlow) {
    offer = offerData;
  }

  React.useEffect(() => {
    aspirexApolloClient.query({
      query: GET_ALL_PROGRAMS,
    }).then((data) => {
      setPrograms(data.data.programs);
    }).catch((error) => {
      logger.error(error);
    });
  }, [aspirexApolloClient]);
  const programName = React.useMemo(() => {
    if (isNull(offer)) {
      return;
    }
    const program = find(programs, (program) => program.id === offer.programId);
    return program ? program?.title : null;
  }, [programs, offer]);

  if (!offer) {
    return null;
  }

  const linkOffer = !offer.isPromoLink && offer.links.length ? first(offer.links) : null;
  const isPromoLink = offer.isPromoLink;
  const promoOffer = first(offer.promos);
  const isMultipleShopifySyncEnabled = promoOffer?.connectedClientMetadata?.some((client) => client.status === CLIENT_CONNECTION_STATUS.ACTIVE) || false;
  const hideInstructions = () => {
    updateInstructionsVisibility(false);
  };
  const showInstructions = () => {
    updateInstructionsVisibility(true);
  };
  const OfferContent = () => {
    const isReadOnly = offer.isReadOnly;
    const offerType = isReadOnly ? PROMO_OFFER_TYPES.LINKED_SHOPIFY_OFFER : PROMO_OFFER_TYPES.AMOUNT_OFF;
    const source = promoOffer ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE;

    const linkOfferUrlWithUtm = linkOffer?.utmFields
      ? `${linkOffer.url}?${chain(linkOffer.utmFields)
        .map((value, key) => `${key}=${value}`)
        .join('&')
        .value()}`
      : `${linkOffer?.url ?? ''}`;

    let offerPurchaseType;
    let offerCodePurchaseRestrictionsRule;
    let recurringCycleLimitAmount;

    if (isPromoLink) {
      offerPurchaseType =
        promoOffer.isOneTime && promoOffer.isSubscription
          ? PurchaseType.BOTH
          : promoOffer.isOneTime
            ? PurchaseType.ONE_TIME
            : promoOffer.isSubscription
              ? PurchaseType.SUBSCRIPTION
              : PurchaseType.ONE_TIME;
      offerCodePurchaseRestrictionsRule =
        promoOffer.recurringCycleLimit === 0
          ? RecurringCycleLimit.DISCOUNT_APPLIES_TO_ALL_RECURRING_PAYMENTS
          : promoOffer.recurringCycleLimit === 1
            ? RecurringCycleLimit.LIMIT_DISCOUNT_TO_THE_FIRST_PAYMENT
            : RecurringCycleLimit.LIMIT_DISCOUNT_TO_MULTIPLE_RECURRING_PAYMENTS;
      recurringCycleLimitAmount = Number(promoOffer.recurringCycleLimit) == 1 ? 0 : Number(promoOffer.recurringCycleLimit);
    }

    return (
      <Card className="p-0 border-none">
        <CardContent className="p-0 space-y-2">
          <div className="flex items-center gap-2">
            <div>
              <RefreshOfferDefaultImage
                imageUrl={offer.imageUrl}
                source={source}
                icon={offer.icon}
                iconClassName="h-6 w-6"
                className="w-16 h-16"
              />
            </div>
            <div>
              <p className="text-primary font-normal m-0 text-xs">{offer.name}</p>
              {source === OFFER_SOURCE.SHOPIFY ? (
                <p className="text-lg text-primary font-medium m-0">
                  {offerType === PROMO_OFFER_TYPES.AMOUNT_OFF && (
                    <>
                      {promoOffer.priceRuleType === OFFER_PRICE_RULE_TYPE.AMOUNT ? '$' : ''}
                      {promoOffer.priceRuleAmount}
                      {promoOffer.priceRuleType === OFFER_PRICE_RULE_TYPE.PERCENTAGE ? '%' : ''}
                      Discount
                    </>
                  )}
                  {offerType === PROMO_OFFER_TYPES.LINKED_SHOPIFY_OFFER && (
                    'Buy X Get Y offer'
                  )}
                </p>
              ) : (
                <TooltipProvider>
                  <ShadCnTooltip>
                    <TooltipTrigger asChild>
                      <p className="text-primary font-medium m-0 text-sm max-w-[300px] truncate cursor-default">
                        {linkOfferUrlWithUtm}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[500px] break-all text-center bg-grey-6 text-grey-0 p-[6px] rounded-lg">
                      <span>{linkOfferUrlWithUtm}</span>
                    </TooltipContent>
                  </ShadCnTooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <CalendarIcon className="h-4 w-4 text-green-4" />
            <p className="text-xs text-grey-6 font-medium m-0">Created on{' '}
              {format(new Date(offer.createdDate.toString()), 'MMM dd, yyyy')}
            </p>
          </div>
          <div>
            {isMultipleShopifySyncEnabled && (
              <ShadCnTag bgColor="bg-grey-6" textColor="text-grey-0" className="rounded-lg" label={<><ShopifyIcon />{' '}<span>Promo Code Sync Enabled</span></>} />
            )}
          </div>
          <div className="border-b border-grey-2" />
          <div>
            {offer.payouts && offer.payouts.length > 0 && (
              <>
                <p className="mb-0 text-xs text-primary font-medium">Commissions</p>
                <div className="flex gap-2 flex-wrap mt-1">
                  {offer.payouts.map((payout, index) => {
                    const label = getPayoutLabel(payout);
                    if (!label) return null;

                    return (
                      <div
                        key={index}
                        className={`${getUniqueRefreshTagColor(index)} text-grey-0 rounded-lg h-[22px] px-2 w-max`}
                      >
                        {label}
                      </div>
                    );
                  })}
                </div>

              </>
            )}
          </div>
          {offer.isPromoLink && (<div className="border-b border-grey-2" />)}
          <div>
            {offer.isPromoLink && (
              <div className="flex items-center gap-2">
                <LinkSimpleIcon fontSize={16} className="text-magenta-4" />
                <p className="text-xs font-medium text-primary m-0">Creators will receive a shareable link</p>
              </div>
            )}
            {offer.isLandingPageEnabled && (
              <div className="flex items-center gap-2">
                <TvIcon fontSize={16} className="text-blue-4" />
                <p className="text-xs font-medium text-primary m-0">Personalized CreatorStore </p>

              </div>
            )}
            {promoOffer?.isSecureCodes && (
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="text-green-4 h-4 w-8" />
                <p className="text-xs font-medium text-primary m-0">A single-use promo code with a string of random digits for security will be generated when customers click the link.</p>
              </div>
            )}

          </div>
          <div className="border-b border-grey-2" />
          <div>
            <p className="text-xs text-primary font-medium m-0">Offer Details</p>
            {promoOffer ? (
              <ul className="list-disc list-inside text-primary text-sm font-normal">
                <li className="text-primary text-sm font-normal">
                  Applies to
                  {' '}
                  {offerPurchaseType === PurchaseType.ONE_TIME ? 'one-time' : offerPurchaseType === PurchaseType.SUBSCRIPTION ? 'Subscription' : 'Subscription and one-time'}
                  {' '}
                  purchase
                </li>
                {offerPurchaseType === PurchaseType.SUBSCRIPTION && (
                  <li className="text-primary text-sm font-normal">
                    {offerCodePurchaseRestrictionsRule === RecurringCycleLimit.LIMIT_DISCOUNT_TO_THE_FIRST_PAYMENT ? 'Limited to 1 recurring payment' : offerCodePurchaseRestrictionsRule === RecurringCycleLimit.LIMIT_DISCOUNT_TO_MULTIPLE_RECURRING_PAYMENTS ? `Limited to ${recurringCycleLimitAmount} recurring payments` : 'For all recurring payments'}
                  </li>
                )}
                {promoOffer.usageLimitRule === OFFER_PROMO_USAGE_LIMIT_RULE.NONE && (<li className="text-primary text-sm font-normal">No minimum purchase requirements</li>)}
                {promoOffer.usageLimitRule === OFFER_PROMO_USAGE_LIMIT_RULE.MINIMUM_PURCHASE_AMOUNT && (
                  <li className="text-primary text-sm font-normal">
                    Minimum purchase of $
                    {' '}
                    {Number(promoOffer.usageLimitAmount).toFixed(2)}
                  </li>
                )}
                {promoOffer.usageLimitRule === OFFER_PROMO_USAGE_LIMIT_RULE.MINIMUM_ITEM_AMOUNT && (
                  <li className="text-primary text-sm font-normal">
                    Minimum purchase of
                    {' '}
                    {promoOffer.usageLimitAmount}
                    {' '}
                    items
                  </li>
                )}
                <li className="text-primary text-sm font-normal">
                  {getUsageLimitMessage(
                    promoOffer.specialLimitOnePerSale,
                    promoOffer.specialLimitUsageCapEnabled,
                    promoOffer.specialLimitNewCustomersOnly,
                    isNull(promoOffer.specialLimitUsageCapAmount)
                      ? null
                      : promoOffer.specialLimitUsageCapAmount.toString()
                  )}
                </li>
                <li className="text-primary text-sm font-normal">
                  {getDiscountCombinationMessage(
                    promoOffer.providerMetadata.discountCombination?.productDiscounts,
                    promoOffer.providerMetadata.discountCombination?.orderDiscounts,
                    promoOffer.providerMetadata.discountCombination?.shippingDiscounts
                  )}
                </li>
                {promoOffer.startDate && (
                  <li className="text-primary text-sm font-normal">
                    Active from{' '}{format(new Date(promoOffer.startDate.toString()), 'MMM dd, yyyy')}

                    {promoOffer.endDate && (
                      <>
                        {' '}
                        until{' '}
                        {format(new Date(promoOffer.endDate.toString()), 'MMM dd, yyyy')}
                      </>
                    )}
                    {!promoOffer.isUngrouped && (
                      <TooltipProvider>
                        <ShadCnTooltip>
                          <TooltipTrigger asChild>
                            <CircleInfoIcon className="h-3 w-3 text-grey-4 cursor-pointer ml-2" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs break-words text-center bg-grey-6 text-grey-0 p-[6px] rounded-lg"><span>The active dates for individual codes may vary when customizing dates for each code is enabled.</span></TooltipContent>
                        </ShadCnTooltip>
                      </TooltipProvider>)}
                  </li>

                )}
              </ul>
            ) :
              (
                <ul className="list-disc list-inside text-primary text-sm font-normal">
                  <li className="text-primary text-sm font-normal">Offer ends on {format(new Date(offer.expirationDate.toString()), 'MMM dd, yyyy')}</li>
                  {linkOffer.creatorDeeplink
                    && (<li className="text-primary text-sm font-normal">Creators are allowed to create custom deeplnks.</li>)}
                  {linkOffer.creatorDeeplink && linkOffer.domains && linkOffer.domains.length > 0 && (
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="multipleDomain">
                        <AccordionTrigger className="text-primary p-0 font-normal ">
                          <li>Other supported domains</li>
                        </AccordionTrigger>
                        <AccordionContent className="pl-5 text-grey-700">
                          <ul className="list-disc list-inside pl-4">
                            {linkOffer.domains.map((domain, index) => (
                              <li key={index}>{domain}</li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </ul>
              )}
          </div>
          <div>
            {programName && (
              <p className="text-primary text-xs font-medium m-0">Connected Project: <span className="text-blue-6">{programName}</span></p>
            )}
          </div>
          <div>
            {!isPromoLink && !isReadOnly && (offer.promos.length > 0 || linkOffer) && (
              <p className="text-grey-6 text-xs font-normal cursor-pointer m-0 underline" onClick={showInstructions}>
                View Setup Instructions
              </p>
            )}
          </div>

        </CardContent>
      </Card>

    );
  };

  const handleClickEditOffer = () => {
    if (offer.links.length && !offer.isPromoLink) {
      if (migrateToGraphQL && isNull(linkOffer.defaultPayoutId)) {
        setShowMigrationModal(true);
      } else {
        history.push({
          ...location,
          pathname: `${baseUri}/offers/${offer.id}/edit`,
          state: {
            isNewFlow: offer.isNewFlow,
          },
        });
      }
    }
    if (offer.promos.length) {
      if (migrateToGraphQL) {
        if (offer.isNewFlow && promoOffer.defaultPayoutId) {
          history.push({
            ...location,
            pathname: `${baseUri}/offers/${offer.id}/edit`,
            state: { isNewFlow: true, isMigrationEnabled: false },
          });
        } else {
          setShowMigrationModal(true);
        }
      } else {
        history.push({
          ...location,
          pathname: `${baseUri}/offers/${offer.id}/edit`,
          state: {
            isNewFlow: offer.isNewFlow,
          },
        });
      }
    }
  };

  const getOfferStatus = (): OFFER_STATUS | null => {
    if (!isEmpty(promoOffer)) {
      const endDate = moment(promoOffer.endDate);
      return endDate < moment() ? OFFER_STATUS.EXPIRED : promoOffer.status;
    } else if (!isEmpty(linkOffer)) {
      const expirationDate = moment(offer.expirationDate);
      return expirationDate < moment() ? OFFER_STATUS.EXPIRED : linkOffer.status;
    }
    return null;
  };

  const OfferTag = () => {
    if (offer.isReadOnly) {
      return (
        <Tag color="#4eb468" size="small">
          Buy X, Get Y
        </Tag>
      );
    }
    if (!isNull(offer.archivedDate)) {
      if (refreshUi) {
        return (
          <Small className="text-grey-4">Your offer is archived</Small>
        );
      }
      return (
        <Badge status="default" text="Your offer is archived" />
      );
    }
    const status = getOfferStatus();
    if (!status) {
      return null;
    }
    const color = getOfferStatusColor(status);
    return (
      <Tag color={color} size="small">
        {getDiscountText(promoOffer, offer.isReadOnly, status)}
      </Tag>
    );
  };
  const handleArchiveOffer = async () => {
    setArchiveOfferLoading(true);
    try {
      await archiveOfferMutation({
        variables: {
          archiveOfferInput: {
            offerId: offer.id,
            userInfo: {
              email: profile.email,
              name: profile.name,
              clientId,

            },
          },
        },
      });
    } catch (error) {
      message.error(error.message || 'Failed to archive offer');
    } finally {
      setArchiveOfferLoading(false);
      setArchiveOfferModal(false);
    }
  };
  const menu = (
    <Menu>
      <Menu.Item key="1" onClick={() => setIsOfferConversionHistoryDrawerVisible(true)} icon={<ChartBarIcon />}>
        Offer Conversions Report
      </Menu.Item>
      <Menu.Item key="2" onClick={() => setIsOfferLogDrawerVisible(true)} icon={<ClockRotateLeftIcon />}>
        Offer Update History
      </Menu.Item>
      {archiveOffer && isNull(offer.archivedDate) && (
        <Menu.Item key="3" danger onClick={() => setArchiveOfferModal(true)} icon={<BoxArchiveIcon />}>
          Archive Offer
        </Menu.Item>
      )}
      {deleteCreatorFromOffer && !(offer.isReadOnly) && (
        <Menu.Item key="deletedMembers" onClick={() => setIsDeletedMembersDrawerVisible(true)} icon={<UserDeleteOutlined />}>
          View Removed Members
        </Menu.Item>
      )}
    </Menu>
  );
  const ActionsButtons = () => {
    if (!showCta) {
      return null;
    }
    return (
      <Row justify="end" gutter={8}>
        <Col>
          <Tooltip title="Offer Settings">
            <Button
              className={styles.offerSettingsButton}
              onClick={handleClickEditOffer}
              size="middle"
              disabled={disableOfferEdit}
              icon={<SettingOutlined />}
            />
          </Tooltip>
        </Col>
        <Col>
          <STAPaymentApp clientId={clientId} clientName={clientName} initialDateFilterSettings={dateRangeSettings} />
        </Col>
        {
          migrateToGraphQL && (
            <Dropdown overlay={menu} placement="bottomLeft">
              <Button icon={<EllipsisIcon />} />
            </Dropdown>
          )
        }
      </Row>
    );
  };
  const RefreshActionsButtons = () => {
    if (!showCta) {
      return null;
    }
    return (
      <div className="md:flex-end flex justify-start space-x-4">
        <div>
          <DateFilter earliestDate={earliestDate} settings={dateRangeSettings} refreshUi={refreshUi} key={1} />
        </div>
        {refreshSTADashboard && (
          <div>
            <HeaderButton
              onClick={() => setIsMemberTableFilterOpen(true)}
              icon={<SlidersIcon className="h-4 w-4" />}
              key={2}
              tooltip="Filter"
            />
          </div>
        )}
        <div>
          <STAPaymentApp clientId={clientId} clientName={clientName} initialDateFilterSettings={dateRangeSettings} refreshUi={refreshUi} />
        </div>
        <div>
          <HeaderButton
            onClick={handleClickEditOffer}
            icon={<SettingOutlined className="h-4 w-4" />}
            key={2}
            tooltip="Offer Settings"
          />
        </div>
        <div>
          {
            migrateToGraphQL && (
              <Dropdown overlay={menu} placement="bottomLeft">
                <ShadCnBtn size="headerIcon" variant="outlineHeader"><EllipsisIcon className="h-4 w-4" /></ShadCnBtn>
              </Dropdown>
            )
          }
        </div>
      </div>
    );
  };

  if (refreshUi) {
    return (
      <>
        <div className="flex flex-col md:flex-row md:justify-between w-full gap-2 md:gap-0">
          <div className="flex flex-wrap items-center gap-2 min-w-0 w-full">
            <HeaderButton
              onClick={goBack}
              icon={<ArrowLeft className="h-4 w-4" />}
            />
            <OfferImage
              className="!w-9 !h-9"
              imageUrl={offer?.imageUrl}
              source={promoOffer ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE}
              iconName={offer?.icon}
            />
            <div className="truncate max-w-[80px] sm:max-w-xs md:max-w-sm">
              {windowWidth < 1090 ? (
                <TooltipProvider>
                  <ShadCnTooltip>
                    <TooltipTrigger asChild>
                      <h1 className="text-primary-foreground m-0 text-base md:text-lg font-inter font-medium truncate cursor-pointer">
                        {truncateText(offer.name, 15)}
                      </h1>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs break-all text-center bg-grey-6 text-grey-0 p-[6px] rounded-lg">
                      <span>{offer.name}</span>
                    </TooltipContent>
                  </ShadCnTooltip>
                </TooltipProvider>
              ) : (
                <h1 className="text-primary-foreground m-0 text-base md:text-lg font-inter font-medium truncate">
                  {offer.name}
                </h1>
              )}
            </div>
            <div>
              {OfferTag()}
            </div>
            <Popover
              content={OfferContent}
              open={visible}
              onOpenChange={setVisible}
              trigger="hover"
              overlayStyle={{ width: '437px' }}
            >
              <button className="text-grey-4">
                <InfoCircleFilled />
              </button>
            </Popover>
          </div>

          <RefreshActionsButtons />
        </div>

        {linkOffer && (
          <ConversionTrackingModal
            offerTrackingType={linkOffer.conversionTrackingType}
            onCancel={hideInstructions}
            pixelCode={linkOffer.pixelCode}
            postbackUrl={linkOffer.postbackUrl}
            visible={instructionsVisible}
          />
        )}
        <OfferLogDrawer
          offerId={offer.id}
          onClose={() => setIsOfferLogDrawerVisible(false)}
          open={isOfferLogDrawerVisible}
          source={promoOffer ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE}
        />

        <OfferConversionHistoryDrawer
          offerId={offer.id}
          onClose={() => setIsOfferConversionHistoryDrawerVisible(false)}
          open={isOfferConversionHistoryDrawerVisible}
          source={isPromoLink ? OFFER_SOURCE.PROMO_LINK : promoOffer ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE}
        />
        {deleteCreatorFromOffer && (
          <DeletedMembersDrawer
            isVisible={isDeletedMembersDrawerVisible}
            onClose={() => setIsDeletedMembersDrawerVisible(false)}
            deletedMembers={deletedMembers}
          />
        )}
        <Modal
          title="Archive Offer"
          open={archiveOfferModal}
          onOk={handleArchiveOffer}
          confirmLoading={archiveOfferLoading}
          onCancel={() => setArchiveOfferModal(false)}
          okText="Archive"
        >
          <Text strong>Are you sure you want to archive this offer?</Text>
          <br />
          <Text>
            {`Please note that all active ${promoOffer ? 'codes' : 'links'} will be`}
            {' '}
            <strong>deactivated</strong>
            {' '}
            {`and moved to the archive. You can still access the analytics for these archived ${promoOffer ? 'codes' : 'links'
              } via the "Archived Offers" tab.`}
          </Text>
          <br />
          <br />
          <Text>Note: This action cannot be undone.</Text>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Row justify="space-between" style={{ width: '100%' }} className={styles.offerDetailsNav}>
        <Row align="middle" gutter={8} className={infoButtonClassName ? styles.infoIconClass : ''}>
          {!hideOfferName && (
            <Row align="middle" gutter={8} className={`${showCta ? '' : styles.customColWidth}`}>
              <Col>
                <OfferImage
                  className={styles.img}
                  imageUrl={offer?.imageUrl}
                  source={promoOffer ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE}
                  iconName={offer?.icon}
                />
              </Col>
              <Col className={`${showCta ? styles.offerDetailsCard : styles.customOfferDetailsWidth}`}>
                <Title level={4} style={{ whiteSpace: 'normal' }}>
                  {offer.name}
                </Title>
              </Col>
              <Col>{OfferTag()}</Col>
            </Row>
          )}
          <Col className={styles.OfferDetailCardContainer}>
            <Popover
              content={OfferContent}
              open={visible}
              onOpenChange={setVisible}
              trigger="hover"
              overlayStyle={{ width: 437 }}
            >
              <Button icon={<InfoCircleFilled />} />
            </Popover>
          </Col>
        </Row>

        <ActionsButtons />
      </Row>
      {linkOffer && (
        <ConversionTrackingModal
          offerTrackingType={linkOffer.conversionTrackingType}
          onCancel={hideInstructions}
          pixelCode={linkOffer.pixelCode}
          postbackUrl={linkOffer.postbackUrl}
          visible={instructionsVisible}
        />
      )}
      <OfferLogDrawer
        offerId={offer.id}
        onClose={() => setIsOfferLogDrawerVisible(false)}
        open={isOfferLogDrawerVisible}
        source={promoOffer ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE}
      />

      <OfferConversionHistoryDrawer
        offerId={offer.id}
        onClose={() => setIsOfferConversionHistoryDrawerVisible(false)}
        open={isOfferConversionHistoryDrawerVisible}
        source={isPromoLink ? OFFER_SOURCE.PROMO_LINK : promoOffer ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE}
      />
      <Modal
        title="Archive Offer"
        open={archiveOfferModal}
        onOk={handleArchiveOffer}
        confirmLoading={archiveOfferLoading}
        onCancel={() => setArchiveOfferModal(false)}
        okText="Archive"
      >
        <Text strong>Are you sure you want to archive this offer?</Text>
        <br />
        <Text>
          {`Please note that all active ${promoOffer ? 'codes' : 'links'} will be`}
          {' '}
          <strong>deactivated</strong>
          {' '}
          {`and moved to the archive. You can still access the analytics for these archived ${promoOffer ? 'codes' : 'links'
            } via the "Archived Offers" tab.`}
        </Text>
        <br />
        <br />
        <Text>Note: This action cannot be undone.</Text>
      </Modal>
    </>
  );
};
